import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks } from "lucide-react";
import type { QuoteListItem } from "@school-sales-ai/shared";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { PageHeader } from "@/components/PageHeader";
import { QuoteStatusBadge } from "@/components/quotes/quote-status";
import { useQuotesTable } from "@/hooks/useQuotes";
import { FILTERABLE_QUOTE_STATUSES, QUOTE_STATUS_META } from "@/lib/quote-status-meta";
import { useQuotesStore } from "@/stores/quotes.store";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** "Cilindros DADCO — U.1500.050 × 10 · y 2 más" — the row has no room for the list. */
function linesSummary(q: QuoteListItem): string {
  const shown = q.linesPreview
    .map((l) => `${l.productName}${l.requestedReference ? ` — ${l.requestedReference}` : ""} × ${l.quantity}`)
    .join(" · ");
  const rest = q.lineCount - q.linesPreview.length;
  return rest > 0 ? `${shown} · y ${rest} más` : shown;
}

/**
 * Cotizaciones — the quote requests the agent sent the team (phase 1a of
 * docs/plans/product-options-and-quotes.md).
 *
 * A table and a detail, no board yet: the board comes in phase 1.5 on an engine
 * extracted from Pedidos. No «+ Nueva»: a request is something a conversation
 * produces. Drafts the agent is still assembling are not listed.
 */
export function Cotizaciones(): React.JSX.Element {
  const search = useQuotesStore((s) => s.search);
  const setSearch = useQuotesStore((s) => s.setSearch);
  const statuses = useQuotesStore((s) => s.statuses);
  const toggleStatus = useQuotesStore((s) => s.toggleStatus);
  const clearStatuses = useQuotesStore((s) => s.clearStatuses);
  const page = useQuotesStore((s) => s.page);
  const setPage = useQuotesStore((s) => s.setPage);
  const { data, isLoading, isError } = useQuotesTable();

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, setSearch]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const statusFilter = (
    <MultiSelectFilter
      options={FILTERABLE_QUOTE_STATUSES.map((s) => ({ id: s, name: QUOTE_STATUS_META[s].label }))}
      selected={statuses}
      // The ids are exactly the statuses offered, so the lookup narrows the type
      // without a cast and an unknown id is ignored.
      onToggle={(id) => {
        const status = FILTERABLE_QUOTE_STATUSES.find((s) => s === id);
        if (status) toggleStatus(status);
      }}
      onClear={clearStatuses}
      placeholder="Estado"
      pluralNoun="estados"
      singularNoun="estado"
      clearLabel="Todos los estados"
      icon={ListChecks}
    />
  );

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <p className="mx-4 rounded-lg border border-dashed border-ink-200 p-8 text-center text-sm text-ink-500 md:mx-0">
        Cargando solicitudes…
      </p>
    );
  } else if (isError) {
    content = (
      <p className="px-4 py-12 text-center text-sm text-red-500 md:px-0 md:text-left">
        Error al cargar las solicitudes. Intenta de nuevo.
      </p>
    );
  } else if (items.length === 0) {
    content = (
      <p className="mx-4 rounded-lg border border-dashed border-ink-200 p-8 text-center text-sm text-ink-500 md:mx-0">
        No hay solicitudes de cotización con estos filtros.
      </p>
    );
  } else {
    content = (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 md:px-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase text-ink-500">
                <th className="py-2 pr-4">Solicitud</th>
                <th className="py-2 pr-4">Prospecto</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4">Qué pide</th>
                <th className="py-2 pr-4">Recibida</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id} className="border-b border-ink-100">
                  <td className="whitespace-nowrap py-3 pr-4">
                    <Link to={`/cotizaciones/${q.id}`} className="font-medium text-ink-900 underline">
                      #{q.number}
                    </Link>
                    {q.complementsNumber !== null && (
                      <p className="text-[11px] text-ink-500">Complementa la #{q.complementsNumber}</p>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <Link to={`/leads/${q.leadId}`} className="text-ink-900 hover:underline">
                      {q.leadName ?? "Sin nombre"}
                    </Link>
                    <p className="font-mono text-[11px] text-ink-500">{q.sessionRef}</p>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    <QuoteStatusBadge status={q.status} />
                  </td>
                  <td className="max-w-[24rem] py-3 pr-4 text-ink-600">
                    <span className="block truncate" title={linesSummary(q)}>
                      {linesSummary(q)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4 text-ink-600">
                    {formatDate(q.submittedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data && data.total > data.pageSize && (
          <div className="mt-4 flex items-center justify-end gap-3 text-sm">
            <span className="text-ink-500">
              {(page - 1) * data.pageSize + 1}–{Math.min(page * data.pageSize, data.total)} de{" "}
              {data.total}
            </span>
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={page * data.pageSize >= data.total}
              onClick={() => setPage(page + 1)}
              className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col md:h-full md:gap-4 md:p-6">
      <div className="md:hidden">
        <PageHeader
          title="Cotizaciones"
          subtitle={isLoading ? undefined : `${total} resultados`}
        />
      </div>

      <div className="hidden items-center gap-3 md:flex">
        <h1 className="font-display text-2xl font-bold text-ink-900">Cotizaciones</h1>
        {!isLoading && (
          <Badge variant="secondary" className="text-xs">
            {total}
          </Badge>
        )}
      </div>

      <div className="flex flex-col gap-2 border-b border-ink-100 bg-white px-4 py-2 md:flex-row md:items-center md:border-0 md:bg-transparent md:p-0">
        <div className="md:w-80">
          <Input
            placeholder="Buscar por nombre, ref, producto o número de parte…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        {statusFilter}
      </div>

      {content}
    </div>
  );
}
